import React, { useId, useMemo, useRef, useState } from "react";
import { IconSearch, IconX, IconUserPlus } from "@tabler/icons-react";
import "./search-select.scss";

// Generic searchable single-select (combobox).
// items: [{ id, title, subtitle, avatar, avatarStyle, keywords }] — keywords is the text the search matches against.
// onCreate(query) + createLabel add a "create new" action (e.g. register a patient who isn't on file yet).
const SearchSelect = ({
  items,
  value,
  onChange,
  label,
  placeholder = "Search",
  hint,
  emptyText,
  maxResults = 8,
  matcher,
  onCreate,
  createLabel = "Add new",
}) => {
  const id = useId();
  const inputRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const selected = items.find((item) => item.id === value);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items.slice(0, maxResults);
    const match = matcher || ((item, text) => item.keywords.toLowerCase().includes(text));
    return items.filter((item) => match(item, q)).slice(0, maxResults);
  }, [items, query, matcher, maxResults]);

  const choose = (item) => {
    onChange(item ? item.id : "");
    setQuery("");
    setOpen(false);
  };

  const create = () => {
    const typed = query.trim();
    setQuery("");
    setOpen(false);
    onCreate(typed);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && open && results[active]) {
      e.preventDefault();
      choose(results[active]);
    } else if (e.key === "Enter" && open && results.length === 0 && onCreate) {
      e.preventDefault();
      create();
    } else if (e.key === "Escape" && open) {
      e.stopPropagation(); // close only the list, not a surrounding dialog
      setOpen(false);
    }
  };

  const avatar = (item) => (
    <span className="avatar" style={item.avatarStyle}>
      {item.avatar}
    </span>
  );

  if (selected) {
    return (
      <div className="search-select">
        <span className="form-field__label">{label}</span>
        <div className="search-select__selected">
          {avatar(selected)}
          <span className="search-select__text">
            <strong>{selected.title}</strong>
            {selected.subtitle && <span>{selected.subtitle}</span>}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            aria-label={`Change ${label.toLowerCase()}`}
            onClick={() => {
              choose(null);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
          >
            <IconX size={16} stroke={2} />
            Change
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="search-select">
      <label className="form-field__label" htmlFor={`${id}-input`}>
        {label}
      </label>
      <div className="search-select__field">
        <IconSearch className="search-select__icon" size={18} stroke={1.75} />
        <input
          ref={inputRef}
          id={`${id}-input`}
          className="form-field__control search-select__input"
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={open && results[active] ? `${id}-opt-${results[active].id}` : undefined}
          autoComplete="off"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
      </div>
      {!open && (hint || onCreate) && (
        <div className="search-select__foot">
          {hint && <span className="form-field__hint">{hint}</span>}
          {onCreate && (
            <button type="button" className="search-select__create-link" onClick={create}>
              <IconUserPlus size={16} stroke={1.75} />
              {createLabel}
            </button>
          )}
        </div>
      )}

      {open && (
        <ul className="search-select__list" id={`${id}-list`} role="listbox" aria-label={`${label} results`}>
          {results.map((item, i) => (
            <li
              key={item.id}
              id={`${id}-opt-${item.id}`}
              role="option"
              aria-selected={i === active}
              className={`search-select__option ${i === active ? "search-select__option--active" : ""}`}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus so blur doesn't close before the click lands
                choose(item);
              }}
              onMouseEnter={() => setActive(i)}
            >
              {avatar(item)}
              <span className="search-select__text">
                <strong>{item.title}</strong>
                {item.subtitle && <span>{item.subtitle}</span>}
              </span>
            </li>
          ))}
          {results.length === 0 && (
            <li className="search-select__empty" role="presentation">
              {emptyText ? emptyText(query) : `Nothing matches "${query}".`}
            </li>
          )}
          {onCreate && (
            <li
              className="search-select__create"
              role="option"
              aria-selected={false}
              onMouseDown={(e) => {
                e.preventDefault();
                create();
              }}
            >
              <span className="search-select__create-icon">
                <IconUserPlus size={18} stroke={1.75} />
              </span>
              <span className="search-select__text">
                <strong>{createLabel}</strong>
                {query.trim() && <span>Start with “{query.trim()}”</span>}
              </span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchSelect;
